// LegalConsent — the "I accept" checkbox on signup (#324).
//
// Unticked by default, and it must stay that way. A pre-ticked box is not
// consent under GDPR (art. 4(11) requires a clear affirmative action, and
// recital 32 rules out silence or pre-ticked boxes explicitly) — so this
// component takes no `defaultChecked` and offers no way to start it ticked.
//
// The links open in a new tab. Someone six steps into a signup form should be
// able to read what they are agreeing to without losing what they have typed;
// sending them away in the same tab makes "read the terms" cost the form.
import React from 'react';
import { cn } from '@/lib/utils';

export const TERMS_PATH = '/villkor';
export const PRIVACY_PATH = '/integritetspolicy';

interface LegalConsentProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    /** Turns the control red — set once the user has tried to submit without it. */
    invalid?: boolean;
    disabled?: boolean;
    id?: string;
}

export const LegalConsent: React.FC<LegalConsentProps> = ({
    checked,
    onChange,
    invalid = false,
    disabled = false,
    id = 'legal-consent',
}) => (
    <div>
        {/* The whole row is the label, so the tap target is the sentence and not
            just a 16px box — this is the last control before "Skapa konto" on a
            phone. */}
        <label
            htmlFor={id}
            className={cn(
                'flex items-start gap-3 rounded-xl border-2 p-3 cursor-pointer transition-colors',
                invalid ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300',
                disabled && 'opacity-60 cursor-not-allowed',
            )}
        >
            <input
                id={id}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0 rounded border-gray-300 text-[#1A4D4F] focus:ring-[#1A4D4F]"
            />
            <span className="text-sm text-gray-700">
                Jag godkänner{' '}
                <a
                    href={TERMS_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                    // Without this the click bubbles to the <label> and toggles
                    // the box — so opening the terms would silently tick the
                    // consent you had not read yet.
                    onClick={(e) => e.stopPropagation()}
                    className="font-medium text-[#1A4D4F] hover:underline"
                >
                    användarvillkoren
                </a>{' '}
                och{' '}
                <a
                    href={PRIVACY_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="font-medium text-[#1A4D4F] hover:underline"
                >
                    integritetspolicyn
                </a>
                .
            </span>
        </label>
        {invalid && (
            <p className="text-sm text-red-600 mt-1.5">
                Du behöver godkänna villkoren för att skapa ett konto.
            </p>
        )}
    </div>
);

export default LegalConsent;
